import React, { useEffect, useState } from 'react';
import {
  getBillingItemsByOrder,
  reviewBillingItem,
  approveBillingItem,
  rejectBillingItem,
  requestChanges
} from '../services/billingItemService';
import { BillingItem, BillingItemStatus } from '../models/billingItem';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Box,
  Typography,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Edit,
  RateReview,
  AssignmentLate
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useRoleCheck } from '../components/Protection';
import { useTranslation } from 'react-i18next';

const allowedRoles = ['ACCOUNTING', 'ADMIN', 'SYSTEM_ADMIN'];

const validStatuses: BillingItemStatus[] = [
  'CREATED',
  'VERIFIED',
  'INVOICED',
  'CANCELLED',
  'PENDING',
  'UNDER_REVIEW',
  'REVIEWED',
  'APPROVED',
  'REJECTED',
  'CHANGES_REQUESTED'
];

const isValidStatus = (status: unknown): status is BillingItemStatus => {
  return (
    typeof status === 'string' &&
    validStatuses.includes(status as BillingItemStatus)
  );
};

const emptyStateStyle = {
  color: 'text.secondary',
  fontStyle: 'italic'
};

const getStatusIcon = (status: BillingItemStatus) => {
  switch (status) {
    case 'APPROVED':
      return <CheckCircle color="success" fontSize="small" />;
    case 'REJECTED':
      return <Cancel color="error" fontSize="small" />;
    case 'UNDER_REVIEW':
      return <HourglassEmpty color="info" fontSize="small" />;
    case 'CHANGES_REQUESTED':
      return <Edit color="warning" fontSize="small" />;
    case 'CREATED':
      return <HourglassEmpty color="action" fontSize="small" />;
    default:
      return null;
  }
};

const getStatusColor = (status: BillingItemStatus) => {
  switch (status) {
    case 'APPROVED':
      return 'success.main';
    case 'REJECTED':
      return 'error.main';
    case 'UNDER_REVIEW':
      return 'info.main';
    case 'CHANGES_REQUESTED':
      return 'warning.main';
    case 'CREATED':
      return 'text.primary';
    default:
      return 'text.secondary';
  }
};

const formatUserId = (userId: string | null | undefined) => {
  if (!userId) return null;
  return `${userId.substring(0, 8)}...`;
};

const BillingItemsPage: React.FC<{ orderId?: number }> = ({ orderId = 1 }) => {
  const { user, loading: authLoading } = useAuth();
  const { hasAnyRole } = useRoleCheck();
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'review' | 'approve' | 'reject' | 'requestChanges' | null;
    item: BillingItem | null;
  }>({ open: false, action: null, item: null });
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const items = await getBillingItemsByOrder(orderId);
      // If items is a paginated response, extract the array
      setBillingItems(Array.isArray(items) ? items : items.content || []);
    } catch (e) {
      setError(t('billingItems.errorLoading'));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line
  }, [orderId]);

  // Helper to get user ID as string (UUID) or return null if not available
  const getUserId = (): string | null => {
    if (user?.userProfile?.id && typeof user.userProfile.id === 'string') {
      return user.userProfile.id;
    }
    if (typeof user?.username === 'string') {
      return user.username as string;
    }
    return null;
  };

  const handleAction = async () => {
    if (!actionDialog.item || !actionDialog.action || !user) return;
    setError(null);
    const userId = getUserId();
    if (!userId) {
      setError(t('billingItems.errorNoUserId'));
      return;
    }
    try {
      let updated: BillingItem | undefined;
      switch (actionDialog.action) {
        case 'review':
          updated = await reviewBillingItem(
            actionDialog.item.id,
            userId,
            comment
          );
          break;
        case 'approve':
          updated = await approveBillingItem(
            actionDialog.item.id,
            userId,
            comment
          );
          break;
        case 'reject':
          updated = await rejectBillingItem(
            actionDialog.item.id,
            userId,
            comment
          );
          break;
        case 'requestChanges':
          updated = await requestChanges(actionDialog.item.id, userId, comment);
          break;
      }
      if (updated) {
        setBillingItems((prev) =>
          prev.map((bi) => (bi.id === updated?.id ? updated : bi))
        );
      }
      setActionDialog({ open: false, action: null, item: null });
      setComment('');
    } catch (e: unknown) {
      const error = e as Error;
      setError(error?.message || t('billingItems.actionFailed'));
    }
  };

  const canReview = (item: BillingItem) => {
    const hasAllowedRole = hasAnyRole(['ACCOUNTING', 'ADMIN', 'SYSTEM_ADMIN']);
    const allowed =
      hasAllowedRole &&
      (item.status === 'CREATED' || item.status === 'CHANGES_REQUESTED');
    return allowed;
  };

  const canApprove = (item: BillingItem) => {
    const userId = getUserId();
    const hasAllowedRole = hasAnyRole(['ACCOUNTING', 'ADMIN', 'SYSTEM_ADMIN']);
    if (typeof item.verifiedBy !== 'string' || userId === null) return false;
    return (
      !!user &&
      hasAllowedRole &&
      item.status === 'UNDER_REVIEW' &&
      item.verifiedBy !== userId
    );
  };

  const canReject = canApprove;
  const canRequestChanges = canApprove;

  const getStatusLabel = (status: BillingItemStatus) => {
    switch (status) {
      case 'APPROVED':
        return t('billingItems.status.approved');
      case 'REJECTED':
        return t('billingItems.status.rejected');
      case 'UNDER_REVIEW':
        return t('billingItems.status.underReview');
      case 'CHANGES_REQUESTED':
        return t('billingItems.status.changesRequested');
      case 'CREATED':
        return t('billingItems.status.created');
      case 'VERIFIED':
        return t('billingItems.status.verified');
      case 'INVOICED':
        return t('billingItems.status.invoiced');
      case 'CANCELLED':
        return t('billingItems.status.cancelled');
      case 'PENDING':
        return t('billingItems.status.pending');
      case 'REVIEWED':
        return t('billingItems.status.reviewed');
      default:
        return t('billingItems.status.unknown');
    }
  };

  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography>{t('billingItems.loadingUser')}</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <Typography>{t('billingItems.mustBeLoggedIn')}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Typography variant="h4" gutterBottom>
          {t('billingItems.title')}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          {t('billingItems.subtitle')}
        </Typography>
        <Box mt={4}>
          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>{t('billingItems.loading')}</Typography>
            </Box>
          ) : (
            <Paper elevation={2} sx={{ width: '100%', overflowX: 'auto' }}>
              <TableContainer sx={{ width: '100%' }}>
                <Table sx={{ minWidth: 900, width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('billingItems.table.id')}</TableCell>
                      <TableCell>{t('billingItems.table.status')}</TableCell>
                      <TableCell>
                        {t('billingItems.table.description')}
                      </TableCell>
                      <TableCell>{t('billingItems.table.reviewer')}</TableCell>
                      <TableCell>{t('billingItems.table.approver')}</TableCell>
                      <TableCell>{t('billingItems.table.comment')}</TableCell>
                      <TableCell>{t('billingItems.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billingItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2
                            }}
                          >
                            <AssignmentLate
                              sx={{ fontSize: 48, color: 'text.secondary' }}
                            />
                            <Typography variant="h6" color="text.secondary">
                              {t('billingItems.noItemsFound')}
                            </Typography>
                            <Typography color="text.secondary">
                              {t('billingItems.noItemsHint')}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      billingItems.map((item) => (
                        <TableRow
                          key={item.id}
                          hover
                          sx={{
                            '&:nth-of-type(odd)': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <TableCell>{item.id}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              {isValidStatus(item.status) &&
                                getStatusIcon(item.status)}
                              <Typography
                                color={
                                  isValidStatus(item.status)
                                    ? getStatusColor(item.status)
                                    : 'text.secondary'
                                }
                              >
                                {isValidStatus(item.status)
                                  ? getStatusLabel(item.status)
                                  : t('billingItems.status.unknown')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                item.description
                                  ? ''
                                  : t(
                                      'billingItems.tooltips.descriptionPending'
                                    )
                              }
                            >
                              <Typography
                                sx={
                                  item.description ? undefined : emptyStateStyle
                                }
                              >
                                {item.description ||
                                  t('billingItems.pendingDescription')}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {item.verifiedBy ? (
                              <Tooltip
                                title={t('billingItems.tooltips.fullId', {
                                  id: item.verifiedBy
                                })}
                              >
                                <Typography>
                                  {formatUserId(item.verifiedBy)}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography sx={emptyStateStyle}>
                                {t('billingItems.notReviewedYet')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.approvedBy ? (
                              <Tooltip
                                title={t('billingItems.tooltips.fullId', {
                                  id: item.approvedBy
                                })}
                              >
                                <Typography>
                                  {formatUserId(item.approvedBy)}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography sx={emptyStateStyle}>
                                {t('billingItems.notApprovedYet')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                item.comment ||
                                t('billingItems.tooltips.noCommentsYet')
                              }
                            >
                              <Typography
                                sx={item.comment ? undefined : emptyStateStyle}
                              >
                                {item.comment || t('billingItems.noComments')}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {canReview(item) && (
                              <Button
                                onClick={() =>
                                  setActionDialog({
                                    open: true,
                                    action: 'review',
                                    item
                                  })
                                }
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<RateReview />}
                              >
                                {t('billingItems.actions.review')}
                              </Button>
                            )}
                            {canApprove(item) && (
                              <Button
                                onClick={() =>
                                  setActionDialog({
                                    open: true,
                                    action: 'approve',
                                    item
                                  })
                                }
                                size="small"
                                variant="outlined"
                                color="success"
                                sx={{ ml: 1 }}
                              >
                                {t('billingItems.actions.approve')}
                              </Button>
                            )}
                            {canReject(item) && (
                              <Button
                                onClick={() =>
                                  setActionDialog({
                                    open: true,
                                    action: 'reject',
                                    item
                                  })
                                }
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ ml: 1 }}
                              >
                                {t('billingItems.actions.reject')}
                              </Button>
                            )}
                            {canRequestChanges(item) && (
                              <Button
                                onClick={() =>
                                  setActionDialog({
                                    open: true,
                                    action: 'requestChanges',
                                    item
                                  })
                                }
                                size="small"
                                variant="outlined"
                                color="warning"
                                sx={{ ml: 1 }}
                              >
                                {t('billingItems.actions.requestChanges')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
          <Dialog
            open={actionDialog.open}
            onClose={() =>
              setActionDialog({ open: false, action: null, item: null })
            }
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {actionDialog.action
                ? t(`billingItems.dialog.title.${actionDialog.action}`)
                : ''}
            </DialogTitle>
            <DialogContent>
              <TextField
                label={t('billingItems.dialog.commentLabel')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                sx={{ mt: 2 }}
                placeholder={t('billingItems.dialog.commentPlaceholder')}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setActionDialog({ open: false, action: null, item: null })
                }
                color="inherit"
              >
                {t('billingItems.dialog.cancel')}
              </Button>
              <Button
                onClick={handleAction}
                variant="contained"
                color={
                  actionDialog.action === 'approve'
                    ? 'success'
                    : actionDialog.action === 'reject'
                    ? 'error'
                    : actionDialog.action === 'requestChanges'
                    ? 'warning'
                    : 'primary'
                }
              >
                {actionDialog.action
                  ? t(`billingItems.dialog.submit.${actionDialog.action}`)
                  : ''}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Container>
  );
};

export default BillingItemsPage;
